/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState } from 'react';
import {
  alpha,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Container,
  Divider,
  Grid,
  IconButton,
  Switch,
  Tab,
  Tabs,
  Typography,
  useTheme
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import EditIcon from '@mui/icons-material/Edit';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router';
import Loader from 'src/common/Loader';
import ConfirmationDialog from 'src/common/ConfirmationDialog';
import { setErrorInfo, setSuccessInfo } from 'src/redux/modalSlice';
import { getTankDetails, updateTank } from 'src/redux/tankSlice';
import { CONFIRMATION_MSG } from 'src/utils/constants';
import ROUTE from 'src/utils/routeConstants';
import useAuth from 'src/context/AuthContext';
import { getFormattedDate, hasAdminLikeAccess } from 'src/utils/getInitials';
import shouldDisplayDetailField from 'src/utils/detailFieldUtils';
import NewTank from './NewTank';
import TankFuelGraph from './TankFuelGraph';
import TankFuelEvents from './TankFuelEvents';
import TankGeneralEvents from './TankGeneralEvents';
import TankStatistics from './TankStatistics';
import TankTransactions from './TankTransactions';
import { useTankDateRange } from './TankDateRangeFilter';

const formatLabel = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

const isISODate = (value) => {
  const isoRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
  return typeof value === 'string' && isoRegex.test(value);
};

const formatValue = (value) => {
  if (value === null || value === undefined) return '--';
  if (typeof value === 'boolean') return value ? 'ON' : 'OFF';
  if (isISODate(value)) return getFormattedDate(value);
  return value;
};

const getDispenserName = (tank) => tank?.dispenser_name
  || tank?.dispenser?.station_name
  || tank?.dispenser?.name
  || tank?.dispenser?.dispenser_name
  || '--';

const shouldRenderField = (key, value) => {
  if (!shouldDisplayDetailField(key)) return false;
  if (typeof value === 'object' && value !== null) return false;

  const excludedFields = [
    'site',
    'current_balance',
    'dispenser_name',
    'dispenser',
    'unit_id',
    'unit_uuid',
    'fuel_graph_enabled',
    'fuel_graph_mode'
  ];
  return !excludedFields.includes(key);
};

const DetailBox = ({ label, children }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        transition: 'all 0.2s',
        '&:hover': {
          borderColor: theme.palette.primary.main,
          bgcolor: alpha(theme.palette.primary.main, 0.02)
        }
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        fontWeight={600}
        sx={{
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          mb: 0.5
        }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
};

function TankDetails() {
  const theme = useTheme();
  const { tankId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { getUser } = useAuth();
  const hasAdminAccess = hasAdminLikeAccess(getUser);

  const { loading, tankDetails } = useSelector((state) => state.tanks);

  const [openModal, setOpenModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const fuelGraphEnabled = Boolean(tankDetails?.fuel_graph_enabled);
  const requestedTab = new URLSearchParams(location.search).get('tab');
  const availableTabs = fuelGraphEnabled
    ? ['details', 'transactions', 'fuel', 'statistics', 'events', 'generalEvents']
    : ['details', 'transactions'];
  const activeTab = availableTabs.includes(requestedTab) ? requestedTab : 'details';
  const isTransactionsTab = activeTab === 'transactions';
  const dateRangeState = useTankDateRange();

  useEffect(() => {
    dispatch(getTankDetails(tankId));
  }, [dispatch, tankId]);

  const detailEntries = useMemo(() => (
    Object.entries(tankDetails || {}).filter(([key, value]) => shouldRenderField(key, value))
  ), [tankDetails]);

  const updateStatus = async () => {
    setShowConfirmModal(false);
    setIsLoading(true);

    try {
      const values = {
        ...tankDetails,
        automation: !tankDetails.automation
      };

      await dispatch(updateTank(values)).unwrap();
      dispatch(setSuccessInfo({ successMsg: 'Tank status updated successfully!' }));
    } catch (error) {
      dispatch(
        setErrorInfo({
          errorMsg:
            error?.response?.data?.error_message
            || error?.message
            || 'Failed to update tank status'
        })
      );
    }

    setIsLoading(false);
  };

  const handleTabChange = (event, value) => {
    const tabQuery = value === 'details' ? '' : `?tab=${value}`;
    history.replace(`${ROUTE.tanks}/${tankId}${tabQuery}`);
  };

  if (loading || isLoading) return <Loader />;

  return (
    <Container
      maxWidth={isTransactionsTab ? false : 'lg'}
      sx={{
        mt: { xs: 1, md: 1.5 },
        mb: 0,
        px: isTransactionsTab ? { xs: 1, sm: 2 } : { xs: 1, sm: 2, md: 3 },
        minHeight: 'calc(100vh - 76px)',
        height: { xs: 'auto', md: 'calc(100vh - 76px)' }
      }}
    >
      <NewTank
        open={openModal}
        initialValues={tankDetails}
        handleClose={() => setOpenModal(false)}
      />

      <Card
        sx={{
          borderRadius: isTransactionsTab ? { xs: 2, md: 0 } : { xs: 2, md: 3 },
          boxShadow: '0 4px 24px 0 rgba(0,0,0,0.05)',
          border: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'none',
          height: { xs: 'auto', md: '100%' },
        }}
      >
        <Box sx={{ position: 'relative', borderBottom: `1px solid ${theme.palette.divider}`, flexShrink: 0 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ px: { xs: 1, sm: 2 }, pr: 7 }}
          >
            <Tab value="details" label="Details" sx={{ textTransform: 'none', fontWeight: 700 }} />
            <Tab value="transactions" label="Transactions" sx={{ textTransform: 'none', fontWeight: 700 }} />
            {fuelGraphEnabled && (
              <Tab
                value="fuel"
                label={tankDetails?.fuel_graph_mode === 'track_fuel' ? 'Track + Fuel' : 'Fuel Graph'}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              />
            )}
            {fuelGraphEnabled && <Tab value="statistics" label="Statistics" sx={{ textTransform: 'none', fontWeight: 700 }} />}
            {fuelGraphEnabled && <Tab value="events" label="Fuel Events" sx={{ textTransform: 'none', fontWeight: 700 }} />}
            {fuelGraphEnabled && <Tab value="generalEvents" label="General Events" sx={{ textTransform: 'none', fontWeight: 700 }} />}
          </Tabs>
          <IconButton
            aria-label="Back to tanks"
            onClick={() => history.replace(ROUTE.tanks)}
            size="small"
            sx={{
              position: 'absolute',
              top: 8,
              right: 24,
              border: '1px solid',
              borderColor: theme.palette.divider,
              bgcolor: 'background.paper'
            }}
          >
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
        </Box>

        <CardContent
          sx={{
            p: isTransactionsTab ? { xs: 1.5, sm: 2 } : { xs: 1.25, sm: 2, md: 2 },
            flex: 1,
            minHeight: 0,
            overflowY: activeTab === 'transactions' ? 'hidden' : 'auto',
            ...(activeTab === 'transactions' && {
              display: 'flex',
              flexDirection: 'column'
            })
          }}
        >
          {activeTab === 'fuel' && <TankFuelGraph tank={tankDetails} dateRangeState={dateRangeState} />}
          {activeTab === 'statistics' && <TankStatistics tank={tankDetails} dateRangeState={dateRangeState} />}
          {activeTab === 'events' && <TankFuelEvents tank={tankDetails} dateRangeState={dateRangeState} />}
          {activeTab === 'generalEvents' && <TankGeneralEvents tank={tankDetails} dateRangeState={dateRangeState} />}
          {activeTab === 'transactions' && (
            <TankTransactions tankId={tankId} siteId={tankDetails?.site} />
          )}
          {activeTab === 'details' && (
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={4} key="dispenser_name">
                <DetailBox label="Dispenser Name">
                  <Typography variant="body1" fontWeight={600}>
                    {getDispenserName(tankDetails)}
                  </Typography>
                </DetailBox>
              </Grid>

              {detailEntries.map(([key, value]) => (
                <Grid item xs={12} sm={6} md={4} key={key}>
                  {key === 'automation' ? (
                    <DetailBox label="Automation">
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography fontWeight={600} color={value ? 'success.main' : 'error.main'}>
                          {value ? 'Enabled' : 'Disabled'}
                        </Typography>
                        <Switch
                          checked={value}
                          onChange={() => hasAdminAccess && setShowConfirmModal(true)}
                          disabled={!hasAdminAccess}
                          color="success"
                        />
                      </Box>
                    </DetailBox>
                  ) : (
                    <DetailBox label={formatLabel(key)}>
                      <Typography variant="body1" fontWeight={600}>
                        {formatValue(value)}
                      </Typography>
                    </DetailBox>
                  )}
                </Grid>
              ))}
            </Grid>
          )}
        </CardContent>

        {hasAdminAccess && (
          <>
            <Divider />
            <CardActions sx={{
              p: 2.5, justifyContent: 'flex-end', position: 'sticky', bottom: 0, zIndex: 2, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider'
            }}
            >
              <Button
                startIcon={<EditIcon />}
                variant="contained"
                onClick={() => setOpenModal(true)}
                sx={{
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Edit Tank Details
              </Button>
            </CardActions>
          </>
        )}
      </Card>

      <ConfirmationDialog
        info={
          tankDetails?.automation
            ? CONFIRMATION_MSG.disableTank
            : CONFIRMATION_MSG.enableTank
        }
        open={showConfirmModal}
        disable={tankDetails?.automation}
        onClose={() => setShowConfirmModal(false)}
        onSubmit={updateStatus}
      />
    </Container>
  );
}

export default TankDetails;
